import { useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { LucideIcon } from 'components/LucideIcon'
import React, { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

const STORAGE_KEY = 'entry-browser-btn-seen'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

enum Phase {
  InitialTextAndIcon = 'initial-text-and-icon',
  AnimationInProgress = 'animation-in-progress',
  FinalIcon = 'final-icon',
}

const wiggle = keyframes`
  0% { transform: rotate(0deg); }
  10% { transform: rotate(-16deg); }
  20% { transform: rotate(14deg); }
  30% { transform: rotate(-12deg); }
  40% { transform: rotate(10deg); }
  50% { transform: rotate(-8deg); }
  60% { transform: rotate(6deg); }
  70% { transform: rotate(-3deg); }
  80% { transform: rotate(1deg); }
  100% { transform: rotate(0deg); }
`

const StyledLink = styled(Link)<{ $phase: Phase }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $phase }) => ($phase === Phase.FinalIcon ? '0' : '0.375rem')};
  height: 32px;
  min-width: 32px;
  padding: ${({ $phase }) =>
    $phase === Phase.FinalIcon ? '0' : '0 0.625rem 0 0.5rem'};
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.activeText};
  background-color: ${({ theme }) => theme.forecast.background2_5};
  border-radius: ${({ $phase }) =>
    $phase === Phase.FinalIcon ? '50%' : '16px'};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  outline: none;
  transition:
    padding 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    gap 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.2s ease;

  ${({ $phase }) =>
    $phase === Phase.AnimationInProgress &&
    css`
      animation: ${wiggle} 0.6s ease-in-out;
    `}

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
    transform: scale(1.05);
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.background4};
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.border2};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`

const Label = styled.span<{ $visible: boolean }>`
  display: inline-block;
  max-width: ${({ $visible }) => ($visible ? '3rem' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow: hidden;
  transition:
    max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const IconWrapper = styled.span`
  display: flex;
  flex-shrink: 0;
`

export const EntryBrowserButton: React.FC = () => {
  const { t } = useLingui()
  const [phase, setPhase] = useState<Phase>(() => {
    try {
      if (
        window.matchMedia(REDUCED_MOTION_QUERY).matches ||
        sessionStorage.getItem(STORAGE_KEY) === 'true'
      ) {
        return Phase.FinalIcon
      }
    } catch {
      return Phase.FinalIcon
    }
    return Phase.InitialTextAndIcon
  })

  useEffect(() => {
    if (phase === Phase.FinalIcon) return

    const wiggleTimer = setTimeout(
      () => setPhase(Phase.AnimationInProgress),
      1200
    )
    const shrinkTimer = setTimeout(() => {
      setPhase(Phase.FinalIcon)
      try {
        sessionStorage.setItem(STORAGE_KEY, 'true')
      } catch {
        /* noop */
      }
    }, 2500)

    return () => {
      clearTimeout(wiggleTimer)
      clearTimeout(shrinkTimer)
    }
    // only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showText = phase !== Phase.FinalIcon

  return (
    <StyledLink
      to='/entry-browser'
      style={{ textDecoration: 'none' }}
      $phase={phase}
      title={t`Entry Browser`}
      aria-label={t`Entry Browser`}>
      <IconWrapper>
        <LucideIcon name='Database' size={18} />
      </IconWrapper>
      <Label $visible={showText}>{t`Entry`}</Label>
    </StyledLink>
  )
}
